// src/components/BuyerEntitiesView.jsx
// Buyer Entity Tracker — LFG corporate entities used in acquisitions
// Tracks formation, tax ID, trade names, governing docs, officers, and documents

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Plus, Building2, FileText, Search,
  Loader2, LogOut, Edit2, Trash2, X, ExternalLink, FolderPlus,
  ChevronDown, ChevronRight, RefreshCw, Upload, Users, MessageSquare,
  UserPlus, Landmark, AlertCircle
} from 'lucide-react';
import { useDisplayColors } from '../lib/ThemeContext';
import { ensureSharePointFolder, listSharePointFolder, supabase } from '../lib/supabase';

// ---------------------------------------------------------------------------
// Co-located hooks (could be moved to useSupabase.js later)
// ---------------------------------------------------------------------------

function useBuyerEntities(organizationId) {
  var _entities = useState([]);
  var entities = _entities[0]; var setEntities = _entities[1];
  var _loading = useState(true);
  var loading = _loading[0]; var setLoading = _loading[1];

  var fetchEntities = useCallback(async function() {
    if (!organizationId) { setLoading(false); return; }
    try {
      var res = await supabase
        .from('buyer_entities')
        .select('*')
        .eq('organization_id', organizationId)
        .order('legal_name', { ascending: true });
      if (res.error) throw res.error;
      setEntities(res.data || []);
    } catch (err) {
      console.error('Error fetching buyer entities:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(function() {
    fetchEntities();
    if (!organizationId) return;
    var channel = supabase
      .channel('buyer-entities-' + organizationId)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_entities', filter: 'organization_id=eq.' + organizationId }, fetchEntities)
      .subscribe();
    return function() { supabase.removeChannel(channel); };
  }, [organizationId, fetchEntities]);

  var createEntity = async function(data) {
    var res = await supabase
      .from('buyer_entities')
      .insert(Object.assign({ organization_id: organizationId }, data))
      .select()
      .single();
    if (res.error) throw res.error;
    await fetchEntities();
    return res.data;
  };

  var deleteEntity = async function(id) {
    var res = await supabase.from('buyer_entities').delete().eq('id', id);
    if (res.error) throw res.error;
    await fetchEntities();
  };

  return { entities: entities, loading: loading, createEntity: createEntity, deleteEntity: deleteEntity, refresh: fetchEntities };
}

function useBuyerEntity(entityId) {
  var _entity = useState(null);
  var entity = _entity[0]; var setEntity = _entity[1];
  var _contacts = useState([]);
  var contacts = _contacts[0]; var setContacts = _contacts[1];
  var _notes = useState([]);
  var notes = _notes[0]; var setNotes = _notes[1];
  var _loading = useState(true);
  var loading = _loading[0]; var setLoading = _loading[1];

  var fetchAll = useCallback(async function() {
    if (!entityId) { setLoading(false); return; }
    try {
      var eRes = await supabase.from('buyer_entities').select('*').eq('id', entityId).single();
      if (eRes.error) throw eRes.error;
      setEntity(eRes.data);

      var cRes = await supabase.from('entity_contacts').select('*').eq('entity_id', entityId).order('created_at');
      setContacts(cRes.data || []);

      var nRes = await supabase.from('entity_notes').select('*').eq('entity_id', entityId).order('created_at', { ascending: false });
      setNotes(nRes.data || []);
    } catch (err) {
      console.error('Error fetching entity:', err);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(function() { fetchAll(); }, [entityId, fetchAll]);

  var updateEntity = async function(updates) {
    var res = await supabase
      .from('buyer_entities')
      .update(Object.assign({}, updates, { updated_at: new Date().toISOString() }))
      .eq('id', entityId);
    if (res.error) throw res.error;
    await fetchAll();
  };

  var addContact = async function(data) {
    var res = await supabase.from('entity_contacts').insert(
      Object.assign({ entity_id: entityId, organization_id: entity && entity.organization_id }, data)
    );
    if (res.error) throw res.error;
    await fetchAll();
  };

  var deleteContact = async function(id) {
    var res = await supabase.from('entity_contacts').delete().eq('id', id);
    if (res.error) throw res.error;
    await fetchAll();
  };

  var addNote = async function(content, createdByName) {
    var res = await supabase.from('entity_notes').insert({
      entity_id: entityId,
      organization_id: entity && entity.organization_id,
      content: content,
      created_by_name: createdByName || 'Unknown'
    });
    if (res.error) throw res.error;
    await fetchAll();
  };

  var deleteNote = async function(id) {
    var res = await supabase.from('entity_notes').delete().eq('id', id);
    if (res.error) throw res.error;
    await fetchAll();
  };

  return {
    entity: entity, contacts: contacts, notes: notes, loading: loading,
    updateEntity: updateEntity, addContact: addContact, deleteContact: deleteContact,
    addNote: addNote, deleteNote: deleteNote, refresh: fetchAll
  };
}

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

var ENTITY_STATUS = {
  active:    { label: 'Active',    color: 'bg-green-100 text-green-700' },
  inactive:  { label: 'Inactive',  color: 'bg-gray-100 text-gray-600' },
  dissolved: { label: 'Dissolved', color: 'bg-red-100 text-red-700' },
};

var ENTITY_TYPES = ['LLC', 'Corporation', 'S-Corporation', 'Partnership', 'LP', 'LLP', 'PC', 'Other'];
var MGMT_STRUCTURES = ['Member-Managed', 'Manager-Managed', 'Board-Managed', 'Officer-Managed', 'Other'];
var SP_SUBFOLDERS = ['Formation Documents', 'Operating Agreement', 'Tax Records', 'Licenses & Registrations', 'Correspondence'];
var SP_BASE = 'Buyer Entities';
var SP_SITE_BASE = 'https://walkerfuneralhomeco.sharepoint.com/sites/Acquisitions/Shared%20Documents/Forms/AllItems.aspx';
var SP_VIEW = '658e47d3-098f-4e46-9bc4-69330c8ad528';

// ---------------------------------------------------------------------------
// Main list view
// ---------------------------------------------------------------------------

export default function BuyerEntitiesView({ organizationId, userProfile, pc, sc, isViewer, onBack, showToast, signOut }) {
  var colors = useDisplayColors(pc, sc);
  var displayPrimary = colors.displayPrimary;
  var displaySecondary = colors.displaySecondary;

  var hook = useBuyerEntities(organizationId);
  var entities = hook.entities;
  var loading = hook.loading;
  var createEntity = hook.createEntity;
  var deleteEntity = hook.deleteEntity;

  var _active = useState(null);
  var activeEntityId = _active[0]; var setActiveEntityId = _active[1];
  var _showNew = useState(false);
  var showNewForm = _showNew[0]; var setShowNewForm = _showNew[1];
  var _search = useState('');
  var searchQuery = _search[0]; var setSearchQuery = _search[1];
  var _filterSt = useState('');
  var filterStatus = _filterSt[0]; var setFilterStatus = _filterSt[1];

  var filtered = entities.filter(function(e) {
    if (filterStatus && e.status !== filterStatus) return false;
    if (!searchQuery.trim()) return true;
    var q = searchQuery.toLowerCase();
    return (
      (e.legal_name || '').toLowerCase().includes(q) ||
      (e.ein || '').toLowerCase().includes(q) ||
      (e.formation_state || '').toLowerCase().includes(q) ||
      (e.dba_name || '').toLowerCase().includes(q)
    );
  });

  if (activeEntityId) {
    return React.createElement(EntityDetailView, {
      entityId: activeEntityId,
      organizationId: organizationId,
      userProfile: userProfile,
      pc: pc, sc: sc,
      isViewer: isViewer,
      onBack: function() { setActiveEntityId(null); },
      showToast: showToast,
      signOut: signOut
    });
  }

  var handleCreate = async function(formData) {
    try {
      var newEntity = await createEntity(formData);
      setShowNewForm(false);
      showToast('Entity created');
      // Auto-create SharePoint folder
      try {
        var fp = SP_BASE + '/' + formData.legal_name;
        await ensureSharePointFolder(fp);
        for (var i = 0; i < SP_SUBFOLDERS.length; i++) {
          await ensureSharePointFolder(fp + '/' + SP_SUBFOLDERS[i]);
        }
        var spPath = '/sites/Acquisitions/Shared Documents/' + fp;
        var spUrl = SP_SITE_BASE + '?id=' + encodeURIComponent(spPath) + '&viewid=' + SP_VIEW;
        await supabase.from('buyer_entities').update({ sharepoint_folder_path: fp, sharepoint_folder_url: spUrl }).eq('id', newEntity.id);
      } catch (spErr) { console.error('SharePoint auto-create failed:', spErr); }
      if (newEntity) setActiveEntityId(newEntity.id);
    } catch (err) { showToast(err.message, 'error'); }
  };

  var handleDelete = async function(e, id, name) {
    e.stopPropagation();
    if (!confirm('Delete "' + name + '"? This cannot be undone.')) return;
    try { await deleteEntity(id); showToast('Entity deleted'); }
    catch (err) { showToast(err.message, 'error'); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading entities...</p>
      </div>
    );
  }

  var activeCount = entities.filter(function(e) { return e.status === 'active'; }).length;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      {/* Page heading */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Buyer Entities
          </h1>
          <p className="text-sm mt-1 tabular-nums" style={{ color: '#475569' }}>
            {entities.length} entit{entities.length === 1 ? 'y' : 'ies'} · LLCs and holding companies used for acquisitions
          </p>
        </div>
        {!isViewer ? (
          <button
            onClick={function() { setShowNewForm(true); }}
            className="h-9 inline-flex items-center gap-1.5 px-3.5 text-sm font-medium rounded-lg text-white transition-all hover:shadow-md"
            style={{
              background: `linear-gradient(135deg, ${pc} 0%, #2E4B7C 100%)`,
              boxShadow: `0 1px 2px ${pc}40, 0 2px 4px ${pc}20`,
            }}
          >
            <Plus className="w-4 h-4" /> New Entity
          </button>
        ) : null}
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <EntityStat label="Active Entities" accent="#1A3665" value={String(activeCount)} />
        <EntityStat label="LLCs" accent="#64748B"
          value={String(entities.filter(function(e) { return e.entity_type === 'LLC'; }).length)} />
        <EntityStat label="EINs on File" accent="#059669"
          value={String(entities.filter(function(e) { return e.ein; }).length)} />
        <EntityStat label="Trade Names" accent="#D97706"
          value={String(entities.filter(function(e) { return e.dba_name; }).length)} />
      </div>

      {/* Filter bar */}
      <div
        className="mb-4 rounded-2xl px-4 py-3 flex gap-3 items-center flex-wrap"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          border: '1px solid #E5E9F0',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
        }}
      >
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} />
          <input
            value={searchQuery}
            onChange={function(e) { setSearchQuery(e.target.value); }}
            placeholder="Search by name, EIN, state…"
            className="w-full h-9 pl-9 pr-3 text-sm rounded-lg focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          />
        </div>
        <select
          value={filterStatus}
          onChange={function(e) { setFilterStatus(e.target.value); }}
          className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none"
          style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="dissolved">Dissolved</option>
        </select>
      </div>

      {/* Entity list */}
      <div className="rounded-2xl overflow-hidden"
           style={{
             backgroundColor: '#ffffff',
             border: '1px solid #E5E9F0',
             boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
           }}>
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.1em]"
             style={{ backgroundColor: '#FAFBFD', borderBottom: '1px solid #F0F2F6', color: '#94A3B8' }}>
          <div className="col-span-3">Legal Name</div>
          <div className="col-span-2">Type / State</div>
          <div className="col-span-2">Formation Date</div>
          <div className="col-span-2">EIN</div>
          <div className="col-span-2">Trade Name (DBA)</div>
          <div className="col-span-1"></div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {searchQuery || filterStatus ? 'No entities match your filters.' : 'No entities yet. Click "New Entity" to add one.'}
          </div>
        ) : (
          filtered.map(function(entity) {
            var st = ENTITY_STATUS[entity.status] || ENTITY_STATUS.active;
            return (
              <div key={entity.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors items-center" onClick={function() { setActiveEntityId(entity.id); }}>
                <div className="col-span-3">
                  <div className="font-medium">{String(entity.legal_name)}</div>
                  <span className={'text-xs px-1.5 py-0.5 rounded ' + st.color}>{String(st.label)}</span>
                </div>
                <div className="col-span-2 text-sm">
                  <div>{entity.entity_type || '—'}</div>
                  {entity.formation_state ? <div className="text-xs text-gray-400">{String(entity.formation_state)}</div> : null}
                </div>
                <div className="col-span-2 text-sm text-gray-600">
                  {entity.formation_date ? new Date(entity.formation_date + 'T00:00:00').toLocaleDateString() : '—'}
                </div>
                <div className="col-span-2 text-sm font-mono text-gray-600">
                  {entity.ein ? String(entity.ein) : <span className="text-gray-300 font-sans">Not set</span>}
                </div>
                <div className="col-span-2 text-sm text-gray-600">
                  {entity.dba_name ? String(entity.dba_name) : '—'}
                </div>
                <div className="col-span-1 flex justify-end">
                  {!isViewer ? (
                    <button onClick={function(e) { handleDelete(e, entity.id, entity.legal_name); }} className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showNewForm ? <NewEntityModal pc={pc} onSave={handleCreate} onClose={function() { setShowNewForm(false); }} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New entity modal
// ---------------------------------------------------------------------------

function EntityStat({ label, value, accent }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>{label}</div>
      <div className="text-xl font-semibold mt-1 tabular-nums leading-none" style={{ color: accent || '#0F172A', letterSpacing: '-0.015em' }}>
        {value}
      </div>
    </div>
  );
}

function NewEntityModal({ pc, onSave, onClose }) {
  var colors = useDisplayColors(pc, null);
  var displayPrimary = colors.displayPrimary;
  var _form = useState({
    legal_name: '', entity_type: 'LLC', status: 'active',
    formation_state: '', formation_date: '',
    ein: '', registered_agent: '',
    dba_name: '', dba_state: '', dba_registration_date: '', dba_expiration_date: '',
    management_structure: 'Member-Managed', governing_doc_date: '', governing_doc_notes: '',
    notes: ''
  });
  var form = _form[0]; var setForm = _form[1];
  var _saving = useState(false);
  var saving = _saving[0]; var setSaving = _saving[1];

  var u = function(f, v) { setForm(function(p) { var n = Object.assign({}, p); n[f] = v; return n; }); };

  var handleSave = async function() {
    if (!form.legal_name.trim()) return;
    setSaving(true);
    try {
      var data = Object.assign({}, form);
      if (!data.formation_date) data.formation_date = null;
      if (!data.governing_doc_date) data.governing_doc_date = null;
      if (!data.dba_registration_date) data.dba_registration_date = null;
      if (!data.dba_expiration_date) data.dba_expiration_date = null;
      if (!data.ein.trim()) data.ein = null;
      if (!data.dba_name.trim()) { data.dba_name = null; data.dba_state = null; data.dba_registration_date = null; data.dba_expiration_date = null; }
      await onSave(data);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: displayPrimary }}>
            <Landmark className="w-5 h-5" /> New Buyer Entity
          </h3>
          <button onClick={onClose} className="hover:bg-gray-100 p-1 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Core */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Entity Information</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1">Legal Entity Name *</label>
                <input value={form.legal_name} onChange={function(e) { u('legal_name', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="e.g. LFG Ohio Acquisition LLC" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Entity Type</label>
                <select value={form.entity_type} onChange={function(e) { u('entity_type', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm">
                  {ENTITY_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Status</label>
                <select value={form.status} onChange={function(e) { u('status', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="dissolved">Dissolved</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Formation State</label>
                <input value={form.formation_state} onChange={function(e) { u('formation_state', e.target.value.toUpperCase().slice(0, 2)); }} maxLength={2} className="w-full border rounded px-3 py-2 text-sm uppercase" placeholder="OH" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Formation Date</label>
                <input type="date" value={form.formation_date} onChange={function(e) { u('formation_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
          {/* Tax */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tax Information</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">EIN (Tax ID)</label>
                <input value={form.ein} onChange={function(e) { u('ein', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm font-mono" placeholder="XX-XXXXXXX" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Registered Agent</label>
                <input value={form.registered_agent} onChange={function(e) { u('registered_agent', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="Agent name or service" />
              </div>
            </div>
          </div>
          {/* Trade Name */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trade Name / DBA (if any)</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1">DBA / Trade Name</label>
                <input value={form.dba_name} onChange={function(e) { u('dba_name', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="Leave blank if none" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Registration State</label>
                <input value={form.dba_state} onChange={function(e) { u('dba_state', e.target.value.toUpperCase().slice(0, 2)); }} maxLength={2} className="w-full border rounded px-3 py-2 text-sm uppercase" placeholder="OH" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Registration Date</label>
                <input type="date" value={form.dba_registration_date} onChange={function(e) { u('dba_registration_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Expiration Date</label>
                <input type="date" value={form.dba_expiration_date} onChange={function(e) { u('dba_expiration_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
          {/* Governing docs */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Governing Documents</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Management Structure</label>
                <select value={form.management_structure} onChange={function(e) { u('management_structure', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm">
                  {MGMT_STRUCTURES.map(function(m) { return <option key={m} value={m}>{m}</option>; })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Governing Doc Date</label>
                <input type="date" value={form.governing_doc_date} onChange={function(e) { u('governing_doc_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1">Governing Doc Notes</label>
                <textarea value={form.governing_doc_notes} onChange={function(e) { u('governing_doc_notes', e.target.value); }} rows={2} className="w-full border rounded px-3 py-2 text-sm resize-none" placeholder="Amendment history, key provisions, etc." />
              </div>
            </div>
          </div>
          {/* Notes */}
          <div>
            <label className="block text-xs font-medium mb-1">Notes</label>
            <textarea value={form.notes} onChange={function(e) { u('notes', e.target.value); }} rows={2} className="w-full border rounded px-3 py-2 text-sm resize-none" placeholder="General notes about this entity" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-50 text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.legal_name.trim()} className="px-4 py-2 text-white rounded hover:opacity-90 text-sm disabled:opacity-50 flex items-center gap-1.5" style={{ backgroundColor: pc }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {saving ? 'Creating...' : 'Create Entity'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entity detail view
// ---------------------------------------------------------------------------

function EntityDetailView(props) {
  var entityId = props.entityId;
  var organizationId = props.organizationId;
  var userProfile = props.userProfile;
  var pc = props.pc; var sc = props.sc;
  var isViewer = props.isViewer;
  var onBack = props.onBack;
  var showToast = props.showToast;
  var signOut = props.signOut;

  var colors = useDisplayColors(pc, sc);
  var displayPrimary = colors.displayPrimary;
  var displaySecondary = colors.displaySecondary;

  var hook = useBuyerEntity(entityId);
  var entity = hook.entity;
  var contacts = hook.contacts;
  var notes = hook.notes;
  var vLoading = hook.loading;
  var updateEntity = hook.updateEntity;
  var addContact = hook.addContact;
  var deleteContact = hook.deleteContact;
  var addNote = hook.addNote;
  var deleteNote = hook.deleteNote;

  var _editing = useState(false);
  var editing = _editing[0]; var setEditing = _editing[1];
  var _editForm = useState({});
  var editForm = _editForm[0]; var setEditForm = _editForm[1];
  var _savingEdit = useState(false);
  var savingEdit = _savingEdit[0]; var setSavingEdit = _savingEdit[1];

  var _newNote = useState('');
  var newNote = _newNote[0]; var setNewNote = _newNote[1];

  var _showAddContact = useState(false);
  var showAddContact = _showAddContact[0]; var setShowAddContact = _showAddContact[1];
  var _newContact = useState({ name: '', role: '', email: '', phone: '' });
  var newContact = _newContact[0]; var setNewContact = _newContact[1];

  var _creatingFolder = useState(false);
  var creatingFolder = _creatingFolder[0]; var setCreatingFolder = _creatingFolder[1];
  var _uploadingFile = useState(false);
  var uploadingFile = _uploadingFile[0]; var setUploadingFile = _uploadingFile[1];
  var _spTree = useState([]);
  var spTree = _spTree[0]; var setSpTree = _spTree[1];
  var _rootFiles = useState([]);
  var rootFiles = _rootFiles[0]; var setRootFiles = _rootFiles[1];
  var _spLoading = useState(false);
  var spLoading = _spLoading[0]; var setSpLoading = _spLoading[1];
  var _expandedFolders = useState({});
  var expandedFolders = _expandedFolders[0]; var setExpandedFolders = _expandedFolders[1];
  var _selectedSubfolder = useState('');
  var selectedSubfolder = _selectedSubfolder[0]; var setSelectedSubfolder = _selectedSubfolder[1];

  // DBA expiry warning
  var dbaExpiringSoon = entity && entity.dba_expiration_date && (function() {
    var days = Math.ceil((new Date(entity.dba_expiration_date) - new Date()) / 86400000);
    return days >= 0 && days <= 90;
  })();
  var dbaExpired = entity && entity.dba_expiration_date && new Date(entity.dba_expiration_date) < new Date();

  useEffect(function() {
    if (entity) {
      setEditForm({
        legal_name: entity.legal_name || '',
        entity_type: entity.entity_type || 'LLC',
        status: entity.status || 'active',
        formation_state: entity.formation_state || '',
        formation_date: entity.formation_date || '',
        ein: entity.ein || '',
        registered_agent: entity.registered_agent || '',
        dba_name: entity.dba_name || '',
        dba_state: entity.dba_state || '',
        dba_registration_date: entity.dba_registration_date || '',
        dba_expiration_date: entity.dba_expiration_date || '',
        management_structure: entity.management_structure || 'Member-Managed',
        governing_doc_date: entity.governing_doc_date || '',
        governing_doc_notes: entity.governing_doc_notes || '',
        notes: entity.notes || '',
      });
    }
  }, [entity]);

  var refreshSpContents = function() {
    if (!entity || !entity.sharepoint_folder_path) return;
    setSpLoading(true);
    listSharePointFolder(entity.sharepoint_folder_path).then(function(res) {
      if (!res || !res.items) { setSpLoading(false); return; }
      var folders = res.items.filter(function(item) { return !!item.folder; });
      var files = res.items.filter(function(item) { return !!item.file; });
      setRootFiles(files.map(function(f) { return { name: f.name, webUrl: f.webUrl, size: f.size, modified: f.lastModifiedDateTime }; }));
      var exp = {};
      folders.forEach(function(f) { exp[f.name] = true; });
      setExpandedFolders(exp);
      Promise.all(folders.map(function(folder) {
        return listSharePointFolder(entity.sharepoint_folder_path + '/' + folder.name).then(function(subRes) {
          var subFiles = (subRes && subRes.items) ? subRes.items.filter(function(i) { return !!i.file; }) : [];
          return { name: folder.name, webUrl: folder.webUrl, files: subFiles.map(function(f) { return { name: f.name, webUrl: f.webUrl, size: f.size, modified: f.lastModifiedDateTime }; }) };
        }).catch(function() { return { name: folder.name, webUrl: folder.webUrl, files: [] }; });
      })).then(function(results) { setSpTree(results); }).finally(function() { setSpLoading(false); });
    }).catch(function(err) { console.error('SP list failed:', err); setSpLoading(false); });
  };

  useEffect(function() {
    refreshSpContents();
  }, [entity && entity.sharepoint_folder_path]);

  var handleSaveEdit = async function() {
    setSavingEdit(true);
    try {
      var data = Object.assign({}, editForm);
      if (!data.formation_date) data.formation_date = null;
      if (!data.governing_doc_date) data.governing_doc_date = null;
      if (!data.dba_registration_date) data.dba_registration_date = null;
      if (!data.dba_expiration_date) data.dba_expiration_date = null;
      if (!data.ein.trim()) data.ein = null;
      await updateEntity(data);
      setEditing(false);
      showToast('Entity updated');
    } catch (err) { showToast(err.message, 'error'); }
    finally { setSavingEdit(false); }
  };

  var handleCreateSharePointFolder = async function() {
    if (!entity) return;
    setCreatingFolder(true);
    try {
      var fp = SP_BASE + '/' + entity.legal_name;
      await ensureSharePointFolder(fp);
      for (var i = 0; i < SP_SUBFOLDERS.length; i++) {
        await ensureSharePointFolder(fp + '/' + SP_SUBFOLDERS[i]);
      }
      var spPath = '/sites/Acquisitions/Shared Documents/' + fp;
      var spUrl = SP_SITE_BASE + '?id=' + encodeURIComponent(spPath) + '&viewid=' + SP_VIEW;
      await updateEntity({ sharepoint_folder_path: fp, sharepoint_folder_url: spUrl });
      showToast('SharePoint folder created');
    } catch (err) { showToast('SharePoint error: ' + err.message, 'error'); }
    finally { setCreatingFolder(false); }
  };

  var handleFileUpload = async function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file || !entity) return;
    setUploadingFile(true);
    try {
      // Upload to SharePoint via sharepoint-sync Edge Function
      if (entity.sharepoint_folder_path) {
        var targetFolder = entity.sharepoint_folder_path;
        if (selectedSubfolder) targetFolder = entity.sharepoint_folder_path + '/' + selectedSubfolder;
        await ensureSharePointFolder(targetFolder);
        var ab = await file.arrayBuffer();
        var b64 = btoa(new Uint8Array(ab).reduce(function(d, b) { return d + String.fromCharCode(b); }, ''));
        var sess = await supabase.auth.getSession();
        var token = sess.data.session && sess.data.session.access_token;
        await fetch(supabase.supabaseUrl + '/functions/v1/sharepoint-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token, 'apikey': supabase.supabaseKey },
          body: JSON.stringify({ action: 'upload-file', folderPath: targetFolder, fileName: file.name, fileContentBase64: b64, mimeType: file.type })
        });
      }
      showToast('File uploaded' + (selectedSubfolder ? ' to ' + selectedSubfolder : ''));
      refreshSpContents();
    } catch (err) { showToast(err.message, 'error'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  var handleAddNote = async function() {
    if (!newNote.trim()) return;
    try {
      var un = (userProfile && userProfile.full_name) || (userProfile && userProfile.email) || 'Unknown';
      await addNote(newNote.trim(), un);
      setNewNote('');
      showToast('Note added');
    } catch (err) { showToast(err.message, 'error'); }
  };

  var handleAddContact = async function() {
    if (!newContact.name.trim()) return;
    try {
      await addContact(newContact);
      setNewContact({ name: '', role: '', email: '', phone: '' });
      setShowAddContact(false);
      showToast('Contact added');
    } catch (err) { showToast(err.message, 'error'); }
  };

  if (vLoading || !entity) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-gray-500 text-sm">Loading entity...</p>
      </div>
    );
  }

  var status = ENTITY_STATUS[entity.status] || ENTITY_STATUS.active;
  var u = function(f, v) { setEditForm(function(p) { var n = Object.assign({}, p); n[f] = v; return n; }); };

  return (
    <div className="w-full p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button onClick={onBack} className="hover:bg-gray-100 p-1 rounded"><ArrowLeft className="w-5 h-5" style={{ color: displaySecondary }} /></button>
            <h1 className="text-2xl font-bold" style={{ color: displayPrimary }}>{String(entity.legal_name)}</h1>
            <span className={'text-xs px-2 py-0.5 rounded ' + status.color}>{String(status.label)}</span>
            {entity.entity_type ? <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{String(entity.entity_type)}</span> : null}
          </div>
          {entity.formation_state ? <p className="text-sm ml-8" style={{ color: displaySecondary }}>Formed in {String(entity.formation_state)}{entity.formation_date ? ' \u2022 ' + new Date(entity.formation_date + 'T00:00:00').toLocaleDateString() : ''}</p> : null}
        </div>
        <div className="flex gap-2">
          {!isViewer && !editing ? <button onClick={function() { setEditing(true); }} className="px-3 py-2 border rounded flex items-center gap-1.5 text-sm hover:bg-gray-50"><Edit2 className="w-4 h-4" /> Edit</button> : null}
          <button onClick={signOut} className="px-3 py-2 bg-gray-100 rounded flex items-center gap-1.5 text-sm hover:bg-gray-200"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>

      {/* DBA expiry warnings */}
      {dbaExpired ? (
        <div className="mb-3 flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          Trade name registration for "{entity.dba_name}" expired {new Date(entity.dba_expiration_date + 'T00:00:00').toLocaleDateString()}
        </div>
      ) : dbaExpiringSoon ? (
        <div className="mb-3 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          Trade name registration for "{entity.dba_name}" expires {new Date(entity.dba_expiration_date + 'T00:00:00').toLocaleDateString()}
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-4">
        {/* Left 2 cols — details + documents */}
        <div className="col-span-2 space-y-4">

          {/* Edit form or detail view */}
          {editing ? (
            <div className="bg-white rounded-lg shadow p-4 space-y-4">
              <h3 className="font-semibold" style={{ color: displayPrimary }}>Edit Entity</h3>

              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Entity Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className="block text-xs mb-1 font-medium">Legal Name</label><input value={editForm.legal_name} onChange={function(e) { u('legal_name', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs mb-1">Entity Type</label><select value={editForm.entity_type} onChange={function(e) { u('entity_type', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm">{ENTITY_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}</select></div>
                  <div><label className="block text-xs mb-1">Status</label><select value={editForm.status} onChange={function(e) { u('status', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm"><option value="active">Active</option><option value="inactive">Inactive</option><option value="dissolved">Dissolved</option></select></div>
                  <div><label className="block text-xs mb-1">Formation State</label><input value={editForm.formation_state} onChange={function(e) { u('formation_state', e.target.value.toUpperCase().slice(0, 2)); }} maxLength={2} className="w-full border rounded px-3 py-2 text-sm uppercase" /></div>
                  <div><label className="block text-xs mb-1">Formation Date</label><input type="date" value={editForm.formation_date} onChange={function(e) { u('formation_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tax Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs mb-1">EIN</label><input value={editForm.ein} onChange={function(e) { u('ein', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm font-mono" placeholder="XX-XXXXXXX" /></div>
                  <div><label className="block text-xs mb-1">Registered Agent</label><input value={editForm.registered_agent} onChange={function(e) { u('registered_agent', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trade Name / DBA</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className="block text-xs mb-1">DBA / Trade Name</label><input value={editForm.dba_name} onChange={function(e) { u('dba_name', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" placeholder="Leave blank if none" /></div>
                  <div><label className="block text-xs mb-1">Registration State</label><input value={editForm.dba_state} onChange={function(e) { u('dba_state', e.target.value.toUpperCase().slice(0, 2)); }} maxLength={2} className="w-full border rounded px-3 py-2 text-sm uppercase" /></div>
                  <div><label className="block text-xs mb-1">Registration Date</label><input type="date" value={editForm.dba_registration_date} onChange={function(e) { u('dba_registration_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs mb-1">Expiration Date</label><input type="date" value={editForm.dba_expiration_date} onChange={function(e) { u('dba_expiration_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Governing Documents</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs mb-1">Management Structure</label><select value={editForm.management_structure} onChange={function(e) { u('management_structure', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm">{MGMT_STRUCTURES.map(function(m) { return <option key={m} value={m}>{m}</option>; })}</select></div>
                  <div><label className="block text-xs mb-1">Governing Doc Date</label><input type="date" value={editForm.governing_doc_date} onChange={function(e) { u('governing_doc_date', e.target.value); }} className="w-full border rounded px-3 py-2 text-sm" /></div>
                  <div className="col-span-2"><label className="block text-xs mb-1">Governing Doc Notes</label><textarea value={editForm.governing_doc_notes} onChange={function(e) { u('governing_doc_notes', e.target.value); }} rows={2} className="w-full border rounded px-3 py-2 text-sm resize-none" /></div>
                </div>
              </div>

              <div><label className="block text-xs mb-1">Notes</label><textarea value={editForm.notes} onChange={function(e) { u('notes', e.target.value); }} rows={2} className="w-full border rounded px-3 py-2 text-sm resize-none" /></div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={function() { setEditing(false); }} className="px-4 py-2 border rounded hover:bg-gray-50 text-sm">Cancel</button>
                <button onClick={handleSaveEdit} disabled={savingEdit} className="px-4 py-2 text-white rounded hover:opacity-90 text-sm disabled:opacity-50" style={{ backgroundColor: pc }}>{savingEdit ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-4 space-y-4">
              {/* Two-column detail grid */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {/* Entity info */}
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">Formation</div>
                  <div className="text-sm font-medium">
                    {[entity.formation_state, entity.formation_date ? new Date(entity.formation_date + 'T00:00:00').toLocaleDateString() : null].filter(Boolean).join(' \u2022 ') || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">EIN / Tax ID</div>
                  <div className="text-sm font-medium font-mono">{entity.ein || <span className="text-gray-300 font-sans">Not set</span>}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">Registered Agent</div>
                  <div className="text-sm font-medium">{entity.registered_agent || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-0.5">Management Structure</div>
                  <div className="text-sm font-medium">{entity.management_structure || '—'}</div>
                </div>
              </div>

              {/* Trade name */}
              {entity.dba_name ? (
                <div className="border-t pt-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Trade Name / DBA</div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                    <div><div className="text-xs text-gray-400">DBA Name</div><div className="text-sm font-medium">{String(entity.dba_name)}</div></div>
                    {entity.dba_state ? <div><div className="text-xs text-gray-400">State</div><div className="text-sm font-medium">{String(entity.dba_state)}</div></div> : null}
                    {entity.dba_registration_date ? <div><div className="text-xs text-gray-400">Registered</div><div className="text-sm">{new Date(entity.dba_registration_date + 'T00:00:00').toLocaleDateString()}</div></div> : null}
                    {entity.dba_expiration_date ? <div><div className="text-xs text-gray-400">Expires</div><div className={'text-sm ' + (dbaExpired ? 'text-red-600 font-medium' : dbaExpiringSoon ? 'text-amber-600' : '')}>{new Date(entity.dba_expiration_date + 'T00:00:00').toLocaleDateString()}</div></div> : null}
                  </div>
                </div>
              ) : null}

              {/* Governing doc notes */}
              {(entity.governing_doc_date || entity.governing_doc_notes) ? (
                <div className="border-t pt-3">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Governing Documents</div>
                  {entity.governing_doc_date ? <div className="text-sm text-gray-600 mb-1">Document Date: {new Date(entity.governing_doc_date + 'T00:00:00').toLocaleDateString()}</div> : null}
                  {entity.governing_doc_notes ? <div className="text-sm whitespace-pre-wrap text-gray-600">{String(entity.governing_doc_notes)}</div> : null}
                </div>
              ) : null}

              {entity.notes ? <div className="border-t pt-3"><div className="text-xs text-gray-400 mb-1">Notes</div><div className="text-sm whitespace-pre-wrap">{String(entity.notes)}</div></div> : null}
            </div>
          )}

          {/* Documents — SharePoint */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: displayPrimary }}><FileText className="w-4 h-4" /> Documents</h3>
              {entity.sharepoint_folder_url ? (
                <div className="flex items-center gap-2">
                  <button onClick={refreshSpContents} disabled={spLoading} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Refresh">
                    <RefreshCw className={'w-3.5 h-3.5' + (spLoading ? ' animate-spin' : '')} />
                  </button>
                  <a href={String(entity.sharepoint_folder_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline"><ExternalLink className="w-3 h-3" /> SharePoint</a>
                </div>
              ) : null}
            </div>

            {entity.sharepoint_folder_url ? (
              <div>
                {spLoading && spTree.length === 0 ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-4 justify-center"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading contents...</div>
                ) : (
                  <div className="border rounded overflow-hidden">
                    {rootFiles.map(function(f) {
                      return (
                        <a key={'root-' + f.name} href={f.webUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-xs text-gray-700 group">
                          <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                          <span className="flex-1 truncate">{String(f.name)}</span>
                          <span className="text-gray-400">{f.modified ? new Date(f.modified).toLocaleDateString() : ''}</span>
                          <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                        </a>
                      );
                    })}
                    {spTree.map(function(folder) {
                      var isOpen = !!expandedFolders[folder.name];
                      return (
                        <div key={folder.name} className="border-b last:border-b-0">
                          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 cursor-pointer select-none"
                            onClick={function() { setExpandedFolders(function(prev) { var next = Object.assign({}, prev); next[folder.name] = !prev[folder.name]; return next; }); }}>
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                            <svg className="w-4 h-4 flex-shrink-0 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                            <span className="text-xs font-medium text-gray-700 flex-1">{String(folder.name)}</span>
                            <span className="text-xs text-gray-400">{folder.files.length > 0 ? String(folder.files.length) + (folder.files.length === 1 ? ' file' : ' files') : 'empty'}</span>
                            <a href={folder.webUrl} target="_blank" rel="noopener noreferrer" className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-blue-600 ml-1" onClick={function(e) { e.stopPropagation(); }} title="Open in SharePoint">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          {isOpen ? (
                            <div className="border-t">
                              {folder.files.length === 0 ? (
                                <div className="px-8 py-2 text-xs text-gray-400 italic">Empty folder</div>
                              ) : (
                                folder.files.map(function(f) {
                                  return (
                                    <a key={f.name} href={f.webUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 pl-8 pr-3 py-2 hover:bg-blue-50 border-b last:border-b-0 text-xs text-gray-700 group">
                                      <FileText className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                                      <span className="flex-1 truncate">{String(f.name)}</span>
                                      <span className="text-gray-400">{f.modified ? new Date(f.modified).toLocaleDateString() : ''}</span>
                                      <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                                    </a>
                                  );
                                })
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {rootFiles.length === 0 && spTree.length === 0 && !spLoading ? (
                      <div className="px-3 py-6 text-xs text-gray-400 text-center">No files in SharePoint yet.</div>
                    ) : null}
                  </div>
                )}
                {!isViewer ? (
                  <div className="mt-3 space-y-2">
                    <select value={selectedSubfolder} onChange={function(e) { setSelectedSubfolder(e.target.value); }} className="w-full border rounded px-2 py-1.5 text-sm">
                      <option value="">Upload to root folder</option>
                      {spTree.map(function(folder) { return <option key={folder.name} value={folder.name}>{String(folder.name)}</option>; })}
                    </select>
                    <label className="px-3 py-1.5 border rounded text-sm cursor-pointer hover:bg-gray-50 inline-flex items-center gap-1.5" style={{ color: displayPrimary, borderColor: pc }}>
                      {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploadingFile ? 'Uploading...' : 'Upload File'}
                      <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt" />
                    </label>
                  </div>
                ) : null}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-3">Create a SharePoint folder to store formation docs, operating agreements, and tax records.</p>
                {!isViewer ? (
                  <button onClick={handleCreateSharePointFolder} disabled={creatingFolder} className="px-3 py-2 border rounded text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50">
                    {creatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
                    {creatingFolder ? 'Creating...' : 'Create SharePoint Folder'}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Right col — contacts + notes */}
        <div className="col-span-1 space-y-4">
          {/* Officers / Contacts */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: displayPrimary }}>
                <Users className="w-4 h-4" /> Officers &amp; Contacts
                <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{String(contacts.length)}</span>
              </h3>
              {!isViewer ? <button onClick={function() { setShowAddContact(true); }} className="text-sm flex items-center gap-1 hover:underline" style={{ color: displayPrimary }}><UserPlus className="w-3 h-3" /> Add</button> : null}
            </div>
            {showAddContact ? (
              <div className="border rounded p-3 mb-3 space-y-2 bg-gray-50">
                <div className="grid grid-cols-2 gap-2">
                  <input value={newContact.name} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { name: e.target.value }); }); }} placeholder="Name *" className="border rounded px-2 py-1.5 text-sm" autoFocus />
                  <input value={newContact.role} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { role: e.target.value }); }); }} placeholder="Role (e.g. Manager, Member)" className="border rounded px-2 py-1.5 text-sm" />
                  <input value={newContact.email} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { email: e.target.value }); }); }} placeholder="Email" className="border rounded px-2 py-1.5 text-sm" />
                  <input value={newContact.phone} onChange={function(e) { setNewContact(function(p) { return Object.assign({}, p, { phone: e.target.value }); }); }} placeholder="Phone" className="border rounded px-2 py-1.5 text-sm" />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={function() { setShowAddContact(false); }} className="px-2 py-1 border rounded text-xs hover:bg-gray-50">Cancel</button>
                  <button onClick={handleAddContact} disabled={!newContact.name.trim()} className="px-2 py-1 text-white rounded text-xs disabled:opacity-50" style={{ backgroundColor: pc }}>Add</button>
                </div>
              </div>
            ) : null}
            {contacts.length === 0 && !showAddContact ? (
              <div className="text-sm text-gray-400 text-center py-4">No contacts yet</div>
            ) : (
              <div className="space-y-2">
                {contacts.map(function(contact) {
                  return (
                    <div key={contact.id} className="border rounded p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-sm font-medium">{String(contact.name)}</div>
                          {contact.role ? <div className="text-xs text-blue-600 font-medium">{String(contact.role)}</div> : null}
                        </div>
                        {!isViewer ? <button onClick={function() { if (confirm('Remove this contact?')) deleteContact(contact.id); }} className="text-red-400 hover:text-red-600 p-1"><X className="w-3 h-3" /></button> : null}
                      </div>
                      <div className="flex flex-col gap-0.5 mt-1.5">
                        {contact.email ? <span className="text-xs text-blue-600">{String(contact.email)}</span> : null}
                        {contact.phone ? <span className="text-xs text-gray-500">{String(contact.phone)}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: displayPrimary }}>
              <MessageSquare className="w-4 h-4" /> Notes
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{String(notes.length)}</span>
            </h3>
            {!isViewer ? (
              <div className="flex gap-2 mb-4">
                <input value={newNote} onChange={function(e) { setNewNote(e.target.value); }} placeholder="Add a note..." className="flex-1 border rounded px-3 py-2 text-sm" onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) handleAddNote(); }} />
                <button onClick={handleAddNote} disabled={!newNote.trim()} className="px-3 py-2 text-white rounded hover:opacity-90 text-sm disabled:opacity-50" style={{ backgroundColor: pc }}>Add</button>
              </div>
            ) : null}
            {notes.length === 0 ? <div className="text-sm text-gray-400 text-center py-4">No notes yet</div> : (
              <div className="space-y-3">
                {notes.map(function(note) {
                  return (
                    <div key={note.id} className="border-l-2 pl-3 py-1" style={{ borderLeftColor: displayPrimary }}>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-400">{String(note.created_by_name || 'Unknown') + ' \u2022 ' + new Date(note.created_at).toLocaleString()}</div>
                        {!isViewer ? <button onClick={function() { deleteNote(note.id); }} className="text-red-400 hover:text-red-600 p-1"><X className="w-3 h-3" /></button> : null}
                      </div>
                      <div className="text-sm mt-0.5 whitespace-pre-wrap">{String(note.content)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
